import { useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { useLiveStreamEvent, useGoLive, getLiveTag, getLiveTags } from '@/hooks/useLiveStreams';
import { useAuthor } from '@/hooks/useAuthor';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useNostr } from '@nostrify/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Layout } from '@/components/Layout';
import { GoLiveModal } from '@/components/GoLiveModal';
import { UploadModal } from '@/components/UploadModal';
import { ZapButton } from '@/components/ZapButton';
import { ZapDialog } from '@/components/ZapDialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/useToast';
import type { NostrEvent } from '@nostrify/nostrify';
import type { Event as NostrToolsEvent } from 'nostr-tools';
import { useOrientationFullscreen } from '@/hooks/useOrientationFullscreen';
import { useLiveStreams } from '@/hooks/useLiveStreams';
import { LiveStreamCard } from '@/components/LiveStreamCard';

/* ── Live chat ─────────────────────────────────────────────────────────── */
function useLiveChat(pubkey: string, dTag: string) {
  const { nostr } = useNostr();
  const aTag = `30311:${pubkey}:${dTag}`;
  return useQuery({
    queryKey: ['live-chat', aTag],
    queryFn: async (ctx) => {
      const events = await nostr.query(
        [{ kinds: [1311], '#a': [aTag], limit: 100 }],
        { signal: ctx.signal },
      );
      return events.sort((a, b) => a.created_at - b.created_at);
    },
    staleTime: 5000,
    refetchInterval: 5000,
  });
}

function ChatMessage({ event }: { event: NostrEvent }) {
  const author = useAuthor(event.pubkey);
  const meta = author.data?.metadata;
  const name = meta?.name ?? event.pubkey.slice(0, 8) + '…';
  const { user } = useCurrentUser();

  // The kind-0 profile event is what we zap to send sats to this user
  const profileEvent = author.data?.event as NostrToolsEvent | undefined;
  const canZap = !!user &&
    user.pubkey !== event.pubkey &&
    !!profileEvent &&
    !!(meta?.lud16 || meta?.lud06);

  return (
    <div className="group flex gap-2 py-1 rounded-lg px-1 -mx-1 transition-colors hover:bg-black/5 dark:hover:bg-white/5">
      <Link to={`/profile/${event.pubkey}`} className="shrink-0">
        <Avatar className="h-6 w-6">
          <AvatarImage src={meta?.picture} />
          <AvatarFallback className="text-[10px] font-bold" style={{ background: 'var(--gb-accent)', color: '#fff' }}>
            {name[0]?.toUpperCase()}
          </AvatarFallback>
        </Avatar>
      </Link>
      <div className="flex-1 min-w-0 leading-snug">
        <Link to={`/profile/${event.pubkey}`} className="text-xs font-bold no-underline hover:underline mr-1.5" style={{ color: 'var(--gb-accent)' }}>
          {name}
        </Link>
        <span className="text-xs break-words" style={{ color: 'var(--text-primary)' }}>{event.content}</span>
      </div>
      {/* Per-message zap button — visible on hover when the user can zap this person */}
      {canZap && profileEvent && (
        <ZapDialog target={profileEvent}>
          <button
            type="button"
            aria-label={`Zap ${name}`}
            className="shrink-0 opacity-100 sm:opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[11px] font-semibold"
            style={{ color: '#f7931a', background: 'rgba(247,147,26,0.12)' }}
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
              <path d="M7 2v11h3v9l7-12h-4l4-8z" />
            </svg>
            Zap
          </button>
        </ZapDialog>
      )}
    </div>
  );
}

/* ── Page ──────────────────────────────────────────────────────────────── */
export default function LiveStreamPage() {
  const { pubkey = '', dTag = '' } = useParams<{ pubkey: string; dTag: string }>();
  const { user } = useCurrentUser();
  const { toast } = useToast();
  const { mutate: publishEvent } = useNostrPublish();
  const { endStream } = useGoLive();
  const queryClient = useQueryClient();

  const [chatMsg, setChatMsg] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [goLiveOpen, setGoLiveOpen] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  useOrientationFullscreen(videoRef);

  const { data: stream, isLoading } = useLiveStreamEvent(pubkey, dTag);
  const { data: chatMessages } = useLiveChat(pubkey, dTag);
  const { data: otherStreams } = useLiveStreams();

  const author = useAuthor(pubkey);
  const authorMeta = author.data?.metadata;

  const title = stream ? (getLiveTag(stream, 'title') ?? 'Untitled Stream') : 'Loading…';
  const streamingUrl = stream ? getLiveTag(stream, 'streaming') : undefined;
  const summary = stream ? getLiveTag(stream, 'summary') : undefined;
  const viewers = stream ? getLiveTag(stream, 'current_participants') : undefined;
  const status = stream ? getLiveTag(stream, 'status') : undefined;
  const tags = stream ? getLiveTags(stream, 't') : [];
  const isLiveNow = status === 'live';
  const isOwner = user?.pubkey === pubkey;
  const authorName = authorMeta?.name ?? authorMeta?.display_name ?? pubkey.slice(0, 8) + '…';
  const aTag = `30311:${pubkey}:${dTag}`;

  useSeoMeta({
    title: `${title} – Goblinbox Live`,
    description: summary ?? `Watch ${authorName} live on Goblinbox`,
  });

  const handleChat = (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !chatMsg.trim()) return;
    publishEvent(
      { kind: 1311, content: chatMsg.trim(), tags: [['a', aTag, '', 'root']] },
      {
        onSuccess: () => {
          setChatMsg('');
          queryClient.invalidateQueries({ queryKey: ['live-chat', aTag] });
        },
      },
    );
  };

  const handleEndStream = () => {
    if (!isOwner || !dTag) return;
    endStream(dTag, pubkey, {
      onSuccess: () => {
        toast({ title: 'Stream ended', description: 'Your stream has been marked as ended.' });
        queryClient.invalidateQueries({ queryKey: ['live-streams'] });
        queryClient.invalidateQueries({ queryKey: ['live-stream-event', pubkey, dTag] });
      },
    });
  };

  const related = otherStreams?.filter(s => getLiveTag(s, 'd') !== dTag || s.pubkey !== pubkey).slice(0, 6) ?? [];

  return (
    <Layout onUploadClick={() => setUploadOpen(true)} onGoLiveClick={() => setGoLiveOpen(true)}>
      <div className="px-4 sm:px-6 py-4" style={{ maxWidth: 1400, margin: '0 auto' }}>
        <div className="flex flex-col xl:flex-row gap-6">

          {/* ── Main column ──────────────────────────────────────── */}
          <div className="flex-1 min-w-0 overflow-hidden">

            {/* Player */}
            <div className="w-full">
              <div
                className="w-full rounded-xl overflow-hidden relative"
                style={{ paddingBottom: '56.25%', background: '#000' }}
              >
                {isLoading ? (
                  <div className="absolute inset-0" style={{ background: 'var(--bg-secondary)' }} />
                ) : streamingUrl ? (
                  <video
                    ref={videoRef}
                    src={streamingUrl}
                    controls
                    autoPlay
                    playsInline
                    className="absolute inset-0 w-full h-full"
                    style={{ objectFit: 'contain', background: '#000' }}
                  />
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-3" style={{ color: 'var(--text-secondary)' }}>
                    <svg width="56" height="56" viewBox="0 0 24 24" fill="currentColor" className="opacity-30">
                      <circle cx="12" cy="12" r="4" />
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" />
                    </svg>
                    <p className="text-sm">{status === 'ended' ? 'Stream has ended' : 'Stream unavailable'}</p>
                  </div>
                )}

                {/* Live indicator overlay */}
                {isLiveNow && !isLoading && (
                  <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-bold text-white" style={{ background: '#f44336' }}>
                    <span className="w-2 h-2 rounded-full bg-white" style={{ animation: 'pulse 1.5s ease-in-out infinite' }} />
                    LIVE
                    {viewers && <span className="ml-1 font-normal opacity-90">· {Number(viewers).toLocaleString()} watching</span>}
                  </div>
                )}
              </div>
            </div>

            {/* Title + status */}
            <div className="flex items-start justify-between gap-3 mt-3 mb-2">
              {isLoading
                ? <Skeleton className="h-7 w-3/4" style={{ background: 'var(--bg-secondary)' }} />
                : (
                  <h1 className="text-lg sm:text-xl font-bold leading-snug" style={{ color: 'var(--text-primary)' }}>
                    {title}
                  </h1>
                )
              }
              {!isLiveNow && status === 'ended' && (
                <span className="shrink-0 text-xs px-2 py-1 rounded font-semibold" style={{ background: 'var(--bg-secondary)', color: 'var(--text-secondary)' }}>
                  Ended
                </span>
              )}
            </div>

            {/* Tags */}
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-3">
                {tags.map(tag => (
                  <Link
                    key={tag}
                    to={`/tag/${tag}`}
                    className="text-xs px-3 py-1 rounded-full no-underline hover:opacity-80"
                    style={{ background: 'var(--bg-secondary)', color: 'var(--gb-accent)' }}
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            )}

            {/* Channel info */}
            {!isLoading && (
              <div
                className="flex items-center gap-3 p-4 rounded-lg mb-4"
                style={{ background: 'var(--bg-secondary)' }}
              >
                <Link to={`/profile/${pubkey}`} className="flex items-center gap-3 flex-1 min-w-0 no-underline">
                  <Avatar className="h-12 w-12 shrink-0">
                    <AvatarImage src={authorMeta?.picture} />
                    <AvatarFallback className="font-bold" style={{ background: 'var(--gb-accent)', color: '#fff' }}>
                      {authorName[0]?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <p className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>{authorName}</p>
                    {summary && (
                      <p className="text-xs mt-0.5 line-clamp-2" style={{ color: 'var(--text-secondary)' }}>{summary}</p>
                    )}
                  </div>
                </Link>

                {/* Zap the stream */}
                {stream && (
                  <ZapButton
                    target={stream as unknown as NostrToolsEvent}
                    className="shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-all hover:opacity-90"
                    showCount
                  />
                )}

                {/* End stream button for owner */}
                {isOwner && isLiveNow && (
                  <button
                    onClick={handleEndStream}
                    className="shrink-0 flex items-center gap-1.5 px-3 py-2 rounded text-sm font-semibold text-white hover:opacity-90 transition-all"
                    style={{ background: '#f44336' }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M6 6h12v12H6z" />
                    </svg>
                    End Stream
                  </button>
                )}
              </div>
            )}

            {/* Related streams */}
            {related.length > 0 && (
              <div className="mt-2">
                <h2 className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: 'var(--text-secondary)' }}>
                  Other Live Streams
                </h2>
                <div className="gb-trending-scroll">
                  {related.map(s => <LiveStreamCard key={s.id} event={s} />)}
                </div>
              </div>
            )}
          </div>

          {/* ── Live Chat sidebar ─────────────────────────────── */}
          <aside
            className="xl:w-80 shrink-0 flex flex-col rounded-xl overflow-hidden"
            style={{ border: '1px solid var(--gb-border)', background: 'var(--bg-secondary)', minHeight: 400, maxHeight: 600 }}
          >
            {/* Chat header */}
            <div
              className="flex items-center gap-2 px-4 py-3 font-semibold text-sm shrink-0"
              style={{ borderBottom: '1px solid var(--gb-border)', color: 'var(--text-primary)' }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ color: '#f44336' }}>
                <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" />
              </svg>
              Live Chat
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-3 py-2 space-y-0.5" style={{ minHeight: 0 }}>
              {chatMessages?.length === 0 && (
                <p className="text-xs text-center py-8" style={{ color: 'var(--text-secondary)' }}>
                  No messages yet. Be the first to chat!
                </p>
              )}
              {chatMessages?.map(msg => <ChatMessage key={msg.id} event={msg} />)}
            </div>

            {/* Chat input */}
            <div className="shrink-0 p-3" style={{ borderTop: '1px solid var(--gb-border)' }}>
              {user ? (
                <form onSubmit={handleChat} className="flex gap-2">
                  <input
                    value={chatMsg}
                    onChange={e => setChatMsg(e.target.value)}
                    placeholder="Say something…"
                    maxLength={500}
                    className="flex-1 text-sm px-3 py-2 rounded-lg outline-none"
                    style={{
                      background: 'var(--bg-primary)',
                      border: '1px solid var(--gb-border)',
                      color: 'var(--text-primary)',
                    }}
                  />
                  <button
                    type="submit"
                    disabled={!chatMsg.trim()}
                    className="px-3 py-2 rounded-lg text-sm font-semibold text-white transition-all hover:opacity-90 disabled:opacity-40"
                    style={{ background: 'var(--gb-accent)' }}
                  >
                    Send
                  </button>
                </form>
              ) : (
                <p className="text-xs text-center" style={{ color: 'var(--text-secondary)' }}>
                  <Link to="/" className="hover:underline" style={{ color: 'var(--gb-accent)' }}>Sign in</Link> to chat
                </p>
              )}
            </div>
          </aside>
        </div>
      </div>

      <UploadModal open={uploadOpen} onClose={() => setUploadOpen(false)} />
      <GoLiveModal open={goLiveOpen} onClose={() => setGoLiveOpen(false)} />
    </Layout>
  );
}
